import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import CloseIcon from 'components/Common/Icons/Close';
import KeyboardArrowDownIcon from 'components/Common/Icons/KeyboardArrowDown';
import TuneIcon from 'components/Common/Icons/Tune';
import BubbleCount from 'components/Listing/Filters/BubbleCount/BubbleCount';
import Button from 'components/UI/Buttons/Button/Button';
import Drawer, {
  DrawerFooter,
  DrawerHeader,
} from 'components/UI/Drawer/Drawer';
import Link from 'components/UI/Link/Link';
import { getUri } from 'services/generic';
import { COLORS } from 'settings/filters';
import {
  FilterType,
  FilterValueType,
} from 'types/ProductListing/ProductListing';

import styles from './FiltersMobile.module.scss';

type PropsType = {
  filters: FilterType[];
  resetUrl: string;
  productCount: number;
  onFilterClick: (filter: FilterType, filterValue: FilterValueType) => void;
  classes?: { toggleButton?: string };
};
type ItemTextType = { label: string };

const TEXT_COMPONENT = {
  Couleur: ColorText,
};

export default function FiltersMobile({
  filters,
  resetUrl,
  productCount,
  onFilterClick,
  classes,
}: PropsType): JSX.Element {
  const { t } = useTranslation(['translation', 'listingPage']);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<number | undefined>();

  function openDrawer() {
    if (!open) setOpen(true);
  }

  function closeDrawer() {
    if (open) {
      setOpen(false);
      setExpanded(undefined);
    }
  }

  function handleExpand(index: number) {
    setExpanded(expanded === index ? undefined : index);
  }

  return (
    <>
      <Button
        onClick={openDrawer}
        className={classNames(styles.button, classes?.toggleButton)}
      >
        {t('LABEL_FILTER')}
        <TuneIcon className={styles.buttonIcon} />
      </Button>
      <Drawer
        open={open}
        onClose={closeDrawer}
        classes={{ paper: styles.drawerPaper }}
      >
        <DrawerHeader className={styles.drawerHeader}>
          <div className={styles.title}>{t('LABEL_FILTER')}</div>
          <Button onClick={closeDrawer} className={styles.close}>
            <CloseIcon />
          </Button>
        </DrawerHeader>
        <div className={styles.drawerInnerContent}>
          {filters.map((filter, index) => {
            const { name, label, values, appliedCount } = filter;
            const TextComponent = TEXT_COMPONENT[label] ?? DefaultText;

            return (
              <Accordion
                key={name}
                expanded={expanded === index}
                onChange={() => handleExpand(index)}
                classes={{ root: styles.accordion, expanded: styles.expanded }}
              >
                <AccordionSummary
                  expandIcon={<KeyboardArrowDownIcon />}
                  classes={{
                    root: styles.accordionSummary,
                    expanded: styles.expanded,
                    content: styles.accordionContent,
                  }}
                >
                  <Typography
                    component="span"
                    className={styles.accordionLabel}
                  >
                    {label}
                  </Typography>
                  {appliedCount > 0 && (
                    <BubbleCount
                      count={appliedCount}
                      className={styles.bubbleCount}
                    />
                  )}
                </AccordionSummary>
                <AccordionDetails classes={{ root: styles.accordionDetails }}>
                  <List classes={{ root: styles.list }}>
                    {values.map(value => {
                      const { label, url, isApplied } = value;

                      return (
                        <ListItem
                          key={label}
                          disablePadding
                          classes={{
                            root: classNames(styles.item, {
                              [styles.selected]: isApplied,
                            }),
                          }}
                        >
                          <Link
                            key={label}
                            to={getUri(url)}
                            onClick={() => onFilterClick(filter, value)}
                            className={styles.link}
                          >
                            <TextComponent label={label} />
                          </Link>
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
        <DrawerFooter className={styles.bottomBar}>
          <Button
            to={resetUrl}
            component={Link}
            model={'secondary'}
            className={styles.resetLink}
          >
            {t('CLEAR')}
          </Button>
          <Button
            onClick={closeDrawer}
            model={'primary'}
            className={styles.seeAllButton}
          >
            {t('SEE_PRODUCTS', {
              ns: 'listingPage',
              count: productCount,
            })}
          </Button>
        </DrawerFooter>
      </Drawer>
    </>
  );
}

function DefaultText({ label }: ItemTextType): JSX.Element {
  return <ListItemText primary={label} classes={{ root: styles.typography }} />;
}

function ColorText({ label }: ItemTextType): JSX.Element {
  return (
    <>
      <div
        className={styles.color}
        style={{ background: COLORS[label.toUpperCase()] ?? label }}
      />
      <DefaultText label={label} />
    </>
  );
}
